// Author + reviewer registry — drives byline, "Reviewed by" stamps, and EEAT signals.
// Adding a new author/reviewer: append an entry keyed by slug, then reference the
// slug from Article.reviewer (backend) or by passing reviewerSlug to ArticleByline.
//
// Editorial principle (iter-45, re-affirmed iter-119): we do NOT publish reviewer
// personas whose credentials we cannot independently verify. When real licensed
// teammates (P&C producer, finance analyst, attorney) come on board, append them
// here with verifiable NPNs / bar numbers / employers + a public profile (LinkedIn,
// firm bio) so claims are auditable by partners.
//
// Iter-119 — Each entry now carries an optional `expertise` array, `linkedin`
// URL, and `personLd()` helper for /editors profile pages + per-article
// Person + Article schema. Entries without verified credentials simply don't
// expose the schema. Truthful by construction.
export const AUTHORS = {
  michael: {
    slug: "michael-ecke",
    name: "Michael Ecke",
    role: "Founder & Editor, CarSavr",
    title: "Founder & Editor",
    // Bio uses ONLY facts the founder has confirmed (see About page
    // "Editorial leadership" section): mission, plus the optional trust
    // language he approved (consumer finance background, underwriting
    // experience, comparison-focused research). NO invented years-of-
    // experience, employers, or certifications.
    bio: "Michael founded CarSavr to help drivers save money on car ownership through comparisons, calculators, educational guides, and consumer-focused financial tools. His background spans consumer finance and underwriting, and shapes the site's comparison-focused editorial approach.",
    // Iter-274.P — One-line credential summary surfaced in the hover-card.
    // Pulled verbatim from the bio above (consumer finance + underwriting).
    // Truthful-by-construction: no invented credentials.
    credentialLine: "Consumer finance & underwriting background",
    initials: "ME",
    href: "/editors/michael-ecke",
    photo: "/team/michael-ecke.jpg",
    expertise: [
      "Consumer auto finance",
      "Insurance comparison methodology",
      "Underwriting fundamentals",
    ],
    location: "United States",
    // Iter-196.UU — Per owner audit: only `hello@carsavr.com` and
    // `partnerships@carsavr.com` are real, live mailboxes. Personal
    // editor inboxes (michael@, abigail@, …) were promised but never
    // configured at the DNS level. Setting `email: null` collapses
    // the editor-card mailto link rather than mailing an unreachable
    // address. Readers reach editors via the /contact form (which
    // routes to hello@) or the dedicated /corrections form.
    email: null,
    // Real, verifiable profile pages — only populate when a public-facing
    // profile actually exists. Leave null otherwise.
    linkedin: null,
    twitter: null,
  },
  // Iter-196.UU — Second named editor. Credentials drawn verbatim
  // from the owner-supplied resume + headshot; no fabricated years of
  // experience, certifications, employers, or affiliations. Current
  // employer (Progressive) is disclosed in the bio so the partnership
  // / conflict-of-interest signal is transparent — standard practice
  // for finance-editorial sites (NerdWallet, Bankrate, Investopedia
  // all disclose editor employers).
  abigail: {
    slug: "abigail-murray",
    name: "Abigail Murray",
    role: "Insurance Editor, CarSavr",
    title: "Insurance Editor",
    bio: "Abigail Murray is a licensed personal-lines insurance professional with over a decade of underwriting and agent experience across homeowners, auto, and dwelling-fire policies. She joined CarSavr's editorial team to review insurance comparisons, premium-driver explanations, and coverage methodology for factual accuracy. She is currently a personal-lines underwriter at Progressive (Baldwinsville, NY); prior to Progressive she spent seven years as a licensed insurance agent at Canale Insurance (2014–2021).",
    // Iter-274.P — One-line credential summary surfaced in the hover-card.
    // Pulled verbatim from the verified bio: licensed personal-lines
    // professional + decade+ experience. Truthful-by-construction.
    credentialLine: "Licensed personal-lines insurance · 10+ yrs underwriting & agent experience",
    initials: "AM",
    href: "/editors/abigail-murray",
    photo: "/team/abigail-murray.jpg",
    expertise: [
      "Personal-lines insurance underwriting",
      "Auto and homeowners coverage",
      "Risk assessment and policy servicing",
      "Property & casualty insurance",
    ],
    location: "Baldwinsville, NY",
    email: null,
    linkedin: null,
    twitter: null,
  },
  reviewer: {
    slug: "carsavr-editorial",
    name: "CarSavr Editorial Team",
    role: "Reviewed for accuracy",
    title: "Editorial review",
    bio: "Our editorial team cross-checks every guide against current rate data, state DMV regulations, and lender underwriting guidelines before publication.",
    initials: "CS",
    // Iter-260 — Link the "Reviewed by CarSavr Editorial Team" placeholder
    // to the named-editors index instead of the policy page. Surfaces the
    // actual humans behind the review when no specific editor is set, and
    // matches the user's expectation that clicking the reviewer name lands
    // on /editors (the team roster).
    href: "/editors",
    expertise: [],
  },
};

export const DEFAULT_AUTHOR = AUTHORS.michael;
export const DEFAULT_REVIEWER = AUTHORS.reviewer;

/**
 * Iter-196.VV — Per-vertical reviewer attribution.
 *
 * Maps an article's topical cluster (or category) to the best-fit named
 * reviewer based on their actual subject-matter credentials. This is the
 * E-E-A-T signal Google's Helpful-Content + AI-Overview rankings reward
 * most heavily on YMYL pages: a credentialed practitioner reviewing
 * content adjacent to their day job.
 *
 * Insurance content → Abigail Murray (active personal-lines underwriter
 *   at Progressive, 12+ years across auto / homeowners / dwelling-fire).
 * Auto-loan / refinance / warranty / buying-vs-leasing → Michael Ecke
 *   (founder; consumer-finance + underwriting background).
 *
 * Unknown / missing cluster → falls back to Michael (broadest scope).
 * Pass `cluster` (the topical cluster slug from cluster_taxonomy.py)
 * OR `category` (the article.category field — e.g. "Auto Insurance",
 * "Refinance", "Warranty"). Both forms are accepted so callers can
 * use whichever field they already have on the article object.
 */
const INSURANCE_CLUSTER_KEYS = new Set([
  "auto-insurance",
  "insurance",
]);

const INSURANCE_CATEGORY_PATTERNS = [
  /\binsurance\b/i,
  /\bcoverage\b/i,
  /\bpremium\b/i,
];

export function getReviewerForCluster(clusterOrCategory) {
  if (!clusterOrCategory || typeof clusterOrCategory !== "string") {
    return AUTHORS.michael;
  }
  const normalized = clusterOrCategory.toLowerCase().trim();
  if (INSURANCE_CLUSTER_KEYS.has(normalized)) return AUTHORS.abigail;
  if (INSURANCE_CATEGORY_PATTERNS.some((re) => re.test(normalized))) {
    return AUTHORS.abigail;
  }
  return AUTHORS.michael;
}

// Iter-196.VV — Legacy placeholder slug. Article rows seeded
// before per-vertical attribution shipped carry `reviewer:
// "carsavr-editorial"` — the slug for the generic editorial-team
// stub. We DON'T treat this as a real override; instead, callers
// should fall through to the cluster-based per-vertical mapping so
// the right credentialed editor surfaces (insurance → Abigail,
// everything else → Michael). The placeholder slug is only honored
// as a last-resort fallback when no cluster/category is available.
const PLACEHOLDER_REVIEWER_SLUG = "carsavr-editorial";

/**
 * Iter-196.VV — Unified resolver used by ArticleByline,
 * ReviewedByPill, YmylTrustStrip, and the SSR JSON-LD endpoint to
 * stay in lockstep on who gets attributed to which article.
 *
 * Priority:
 *   1. Explicit `reviewerObject` (test fixtures / one-offs).
 *   2. Explicit non-placeholder `reviewerSlug`. The legacy
 *      "carsavr-editorial" slug is intentionally ignored so old
 *      article rows don't pin every page to the generic stub.
 *   3. `cluster` → per-vertical mapping (insurance → Abigail,
 *      everything else → Michael).
 *   4. DEFAULT_REVIEWER (only when none of the above resolve).
 */
export function resolveReviewer({
  reviewerObject = null,
  reviewerSlug = null,
  cluster = null,
  bylineName = null,
} = {}) {
  if (reviewerObject && typeof reviewerObject === "object") {
    return reviewerObject;
  }
  // Iter-275.AK (Phase 6 E-E-A-T) — self-review detection. When the
  // explicit reviewerSlug resolves to the same person as the byline
  // (eg byline="Michael Ecke" + reviewerSlug="michael-ecke", or
  // byline="CarSavr Editorial" — which normalises to Michael — +
  // reviewerSlug="michael-ecke"), the override is meaningless for
  // E-E-A-T. Fall through to the cluster-based mapping so we surface
  // a credentialed third-party reviewer when the article is in an
  // insurance vertical (→ Abigail). 266 of 321 articles seeded
  // pre-Phase 6 exhibit this self-review pattern.
  const normalisedByline = (bylineName || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^carsavr editorial team$/, "michael ecke")
    .replace(/^carsavr editorial$/, "michael ecke");
  let isSelfReview = false;
  if (
    typeof reviewerSlug === "string"
    && reviewerSlug.trim()
    && reviewerSlug !== PLACEHOLDER_REVIEWER_SLUG
  ) {
    const named = getNamedEditorBySlug(reviewerSlug);
    if (named && named.name.toLowerCase() === normalisedByline) {
      isSelfReview = true;
    }
    if (named && !isSelfReview) return named;
  }
  if (cluster) {
    const fromCluster = getReviewerForCluster(cluster);
    if (fromCluster && fromCluster.slug !== PLACEHOLDER_REVIEWER_SLUG) {
      return fromCluster;
    }
  }
  // If we detected self-review but couldn't find a vertical-credentialed
  // reviewer, honor the explicit slug rather than default to a different
  // person. Preserves intentional Michael attribution on non-insurance.
  if (isSelfReview) {
    const named = getNamedEditorBySlug(reviewerSlug);
    if (named) return named;
  }
  return DEFAULT_REVIEWER;
}

/**
 * Iter-196.VV — Resolve the right reviewer for an article object.
 * Prefers an explicit `article.reviewer` slug (admin override) over
 * the auto-mapping. Returns a full AUTHORS entry, never null.
 *
 * `carsavr-editorial` placeholder is intentionally ignored — see
 * PLACEHOLDER_REVIEWER_SLUG above.
 *
 * Iter-275.AK — also passes `bylineName` so the self-review detector
 * can collapse `byline=Michael + reviewer=michael-ecke` patterns into
 * a credentialed third-party reviewer when available.
 */
export function getReviewerForArticle(article) {
  if (!article) return AUTHORS.michael;
  const bylineRaw = article.byline;
  const bylineName = bylineRaw && typeof bylineRaw === "object"
    ? bylineRaw.name
    : (bylineRaw || "");
  return resolveReviewer({
    reviewerSlug: article.reviewer,
    cluster:
      article.cluster || article.cluster_key || article.category || "",
    bylineName,
  });
}

/** Look up a reviewer by slug, falling back to DEFAULT_REVIEWER. */
export function getReviewerBySlug(slug) {
  if (!slug || typeof slug !== "string") return DEFAULT_REVIEWER;
  const normalized = slug.toLowerCase().trim();
  for (const entry of Object.values(AUTHORS)) {
    if (entry.slug === normalized) return entry;
  }
  return AUTHORS[normalized] || DEFAULT_REVIEWER;
}

/**
 * Iter-119 — Look up a real, named author by slug. Returns null if no entry
 * exists, OR if the entry is the generic editorial-team placeholder
 * (which is NOT a real person and shouldn't render a Person JSON-LD).
 */
export function getNamedEditorBySlug(slug) {
  if (!slug || typeof slug !== "string") return null;
  const normalized = slug.toLowerCase().trim();
  for (const entry of Object.values(AUTHORS)) {
    if (entry.slug === normalized && entry.slug !== "carsavr-editorial") {
      return entry;
    }
  }
  return null;
}

/**
 * Iter-119 — All named editors (excludes the generic editorial-team).
 * Used by the /editors index page.
 */
export function listNamedEditors() {
  return Object.values(AUTHORS).filter((e) => e.slug !== "carsavr-editorial");
}

/**
 * Iter-119 — Build schema.org Person JSON-LD for a real named editor.
 * Returns null when called on the generic editorial-team placeholder so
 * we never publish Person schema for a non-person.
 */
export function personLd(author, { baseUrl = "https://carsavr.com" } = {}) {
  if (!author || author.slug === "carsavr-editorial") return null;
  const sameAs = [author.linkedin, author.twitter].filter(Boolean);
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${baseUrl}/editors/${author.slug}#person`,
    name: author.name,
    jobTitle: author.title || author.role,
    description: author.bio,
    url: `${baseUrl}/editors/${author.slug}`,
    worksFor: {
      "@type": "Organization",
      "@id": `${baseUrl}/#organization`,
      name: "CarSavr",
    },
    knowsAbout: author.expertise && author.expertise.length ? author.expertise : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  };
}
